import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import recallService from '../../../../services/recall.service';
import {
    canDownloadInitialReport,
    canUploadRecallFile,
    extractFileName,
    formatRecallStatusLabel,
    hasInitialReport,
    hasUploadedFile,
    RECALL_STATUS_META,
} from './recallsQuery';
import RecallInitialReportButton from './RecallInitialReportButton';
import RecallUploadButton from './RecallUploadButton';

const STATUS_STEPS = [
    { key: 'PENDING', label: 'Submitted', icon: 'bi-send' },
    { key: 'IN_PROCESS', label: 'Processing', icon: 'bi-arrow-repeat' },
    { key: 'COMPLETED', label: 'Complete', icon: 'bi-check-circle' },
];

function RecallStatusBadge({ status, large = false }) {
    const meta = RECALL_STATUS_META[status] || RECALL_STATUS_META.PENDING;

    return (
        <span className={`recall-status ${meta.className}${large ? ' recall-status--lg' : ''}`}>
            <i className={`bi ${meta.icon}`}></i>
            {formatRecallStatusLabel(status)}
        </span>
    );
}

function MetaItem({ icon, label, value }) {
    if (!value) {
        return null;
    }

    return (
        <div className="recall-details-modal__meta-item">
            <span className="recall-details-modal__meta-label">
                <i className={`bi ${icon} me-1`}></i>
                {label}
            </span>
            <span className="recall-details-modal__meta-value">{value}</span>
        </div>
    );
}

function RecallDetailsSkeleton() {
    return (
        <div className="recall-details-modal__skeleton">
            <div className="recall-details-modal__skeleton-line recall-details-modal__skeleton-line--lg" />
            <div className="recall-details-modal__skeleton-line" />
            <div className="recall-details-modal__skeleton-line recall-details-modal__skeleton-line--md" />
            <div className="recall-details-modal__skeleton-card" />
        </div>
    );
}

function StatusTimeline({ status }) {
    const isFailed = status === 'FAILED';
    const activeIndex = isFailed
        ? 2
        : status === 'COMPLETED'
          ? 2
          : status === 'IN_PROCESS'
            ? 1
            : 0;

    return (
        <div className="recall-details-modal__timeline">
            {STATUS_STEPS.map((step, index) => {
                const isActive = index <= activeIndex && !isFailed;
                const isCurrent = index === activeIndex && !isFailed;
                const isFailedStep = isFailed && index === 2;

                return (
                    <div
                        key={step.key}
                        className={`recall-details-modal__timeline-step${
                            isActive ? ' is-complete' : ''
                        }${isCurrent ? ' is-current' : ''}${isFailedStep ? ' is-failed' : ''}`}
                    >
                        <span className="recall-details-modal__timeline-icon">
                            <i className={`bi ${isFailedStep ? 'bi-x-circle-fill' : step.icon}`}></i>
                        </span>
                        <span className="recall-details-modal__timeline-label">{step.label}</span>
                    </div>
                );
            })}
        </div>
    );
}

function FileStatusCard({ item }) {
    const uploaded = hasUploadedFile(item);
    const fileName = extractFileName(item.filePath);

    return (
        <div className={`recall-details-modal__file-card${uploaded ? ' is-uploaded' : ' is-missing'}`}>
            <span className="recall-details-modal__file-icon">
                <i className={`bi ${uploaded ? 'bi-file-earmark-check' : 'bi-file-earmark-arrow-up'}`}></i>
            </span>
            <div className="recall-details-modal__file-copy">
                <span className="recall-details-modal__file-title">
                    {uploaded ? 'Recall file uploaded' : 'Recall file required'}
                </span>
                <span className="recall-details-modal__file-meta">
                    {uploaded
                        ? fileName
                        : 'Upload a csv, doc, docx, or pdf file so EyeconX can process this batch.'}
                </span>
            </div>
        </div>
    );
}

function NotePanel({ label, icon, text, variant = 'default' }) {
    if (!text) {
        return null;
    }

    return (
        <div className={`recall-details-modal__note recall-details-modal__note--${variant}`}>
            <div className="recall-details-modal__note-header">
                <i className={`bi ${icon}`}></i>
                <span>{label}</span>
            </div>
            <p className="recall-details-modal__note-text mb-0">{text}</p>
        </div>
    );
}

function RecallDetailsModal({ show, onClose, clinicId, requestId, previewItem, onUpdated }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    const loadDetail = async () => {
        if (!clinicId || !requestId) {
            return;
        }

        const response = await recallService.getRecallRequestById(clinicId, requestId);
        setDetail(response.data);
    };

    useEffect(() => {
        if (!show || !clinicId || !requestId) {
            setDetail(null);
            setLoadFailed(false);
            return undefined;
        }

        let active = true;
        setLoading(true);
        setLoadFailed(false);
        setDetail(previewItem || null);

        recallService
            .getRecallRequestById(clinicId, requestId)
            .then((response) => {
                if (active) {
                    setDetail(response.data);
                }
            })
            .catch((error) => {
                console.error('Failed to load recall request', error);
                if (active) {
                    toast.error('Failed to load recall request details');
                    setLoadFailed(true);
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [show, clinicId, requestId, previewItem]);

    const handleUploaded = async () => {
        try {
            await loadDetail();
            onUpdated?.();
        } catch (error) {
            console.error('Failed to refresh recall request', error);
        }
    };

    const item = useMemo(
        () => ({ ...(previewItem || {}), ...(detail || {}) }),
        [previewItem, detail]
    );

    if (!show) {
        return null;
    }

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="lg"
            scrollable
            backdrop="static"
            className="recall-details-modal ecx-page"
            contentClassName="recall-details-modal__content border-0 shadow-lg"
        >
            <Modal.Header className="recall-details-modal__header border-0 pb-0">
                <div className="w-100">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                        <div className="d-flex align-items-start gap-3 min-width-0">
                            <span className="recall-details-modal__icon-badge flex-shrink-0">
                                <i className="bi bi-person-check"></i>
                            </span>
                            <div className="min-width-0">
                                <span className="ecx-page-chip mb-2">
                                    <i className="bi bi-arrow-repeat"></i>
                                    Recall Request
                                </span>
                                <Modal.Title as="h5" className="fw-bold text-dark mb-2 text-break">
                                    {item.title || 'Recall request details'}
                                </Modal.Title>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    {item.status && <RecallStatusBadge status={item.status} />}
                                    {item.createdAt && (
                                        <span className="recall-details-modal__header-meta">
                                            <i className="bi bi-calendar3 me-1"></i>
                                            {item.createdAt}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-3 ecx-modal__close-btn flex-shrink-0"
                            onClick={onClose}
                            aria-label="Close"
                            title="Close"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="recall-details-modal__body pt-3">
                {loading && !item.title ? (
                    <RecallDetailsSkeleton />
                ) : loadFailed && !item.title ? (
                    <div className="recall-details-modal__empty">
                        <span className="recall-details-modal__empty-icon">
                            <i className="bi bi-exclamation-circle"></i>
                        </span>
                        <h6 className="fw-semibold text-dark mb-1">Details unavailable</h6>
                        <p className="text-muted mb-0 small">
                            We could not load this recall request. Try again later.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="recall-details-modal__summary mb-3">
                            <div className="recall-details-modal__summary-grid">
                                <MetaItem icon="bi-calendar-plus" label="Created" value={item.createdAt} />
                                <MetaItem icon="bi-clock-history" label="Last updated" value={item.modifiedAt} />
                                <MetaItem
                                    icon="bi-hash"
                                    label="Request ID"
                                    value={item.id ? String(item.id) : null}
                                />
                            </div>
                        </div>

                        <div className="recall-details-modal__panel mb-3">
                            <div className="recall-details-modal__panel-title">Processing status</div>
                            <StatusTimeline status={item.status} />
                        </div>

                        <div className="recall-details-modal__panel mb-3">
                            <div className="recall-details-modal__panel-title">Recall file</div>
                            <FileStatusCard item={item} />
                        </div>

                        {(item.notes || item.adminNotes) && (
                            <div className="recall-details-modal__panel">
                                <div className="recall-details-modal__panel-title">Notes</div>
                                <div className="recall-details-modal__notes-stack">
                                    <NotePanel
                                        label="Clinic notes"
                                        icon="bi-journal-text"
                                        text={item.notes}
                                        variant="clinic"
                                    />
                                    <NotePanel
                                        label="EyeconX team notes"
                                        icon="bi-chat-square-text"
                                        text={item.adminNotes}
                                        variant="admin"
                                    />
                                </div>
                            </div>
                        )}

                        {canDownloadInitialReport(item) && (
                            <div className="recall-details-modal__panel mt-3">
                                <div className="recall-details-modal__panel-title">Initial report</div>
                                <div className="recall-details-modal__report-banner">
                                    <div className="recall-details-modal__report-banner-content">
                                        <i className="bi bi-file-earmark-pdf"></i>
                                        <div>
                                            <div className="recall-details-modal__report-banner-title">
                                                Processing report
                                            </div>
                                            <div className="recall-details-modal__report-banner-text">
                                                {hasInitialReport(item)
                                                    ? 'Download the initial report generated after your recall file was processed.'
                                                    : 'The initial report will be available once processing begins.'}
                                            </div>
                                        </div>
                                    </div>
                                    {hasInitialReport(item) && (
                                        <RecallInitialReportButton
                                            clinicId={clinicId}
                                            recallId={requestId}
                                            request={item}
                                            variant="primary"
                                            label="Download PDF"
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="recall-details-modal__footer border-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {canUploadRecallFile(item) && (
                            <RecallUploadButton
                                clinicId={clinicId}
                                recallId={requestId}
                                onUploaded={handleUploaded}
                                showLabel
                                label="Upload recall file"
                                variant="primary"
                            />
                        )}

                        {hasInitialReport(item) && (
                            <RecallInitialReportButton
                                clinicId={clinicId}
                                recallId={requestId}
                                request={item}
                                showLabel
                                label="Download initial report"
                                variant="light-primary"
                            />
                        )}

                        {hasUploadedFile(item) && item.status === 'PENDING' && (
                            <span className="recall-details-modal__footer-hint text-muted small">
                                <i className="bi bi-check-circle me-1"></i>
                                File uploaded — awaiting EyeconX processing
                            </span>
                        )}
                    </div>

                    <button
                        type="button"
                        className="btn btn-outline-secondary rounded-pill px-3 ecx-modal__footer-close-btn"
                        onClick={onClose}
                    >
                        <i className="bi bi-x-circle me-1"></i>
                        Close
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default RecallDetailsModal;
